<script>
// ★ [E-72, sobre o E-64] Cartão de DOWNLOAD do arquivo que o agente entregou.
//
// **O defeito que este componente fecha — e ele não é de código, é de
// promessa.** O consultor pede "me manda uma planilha com a minha fila de
// priorização"; o agente gera o `.xlsx`, grava em S3, publica o recibo; o
// backend entrega o arquivo byte a byte pela rota pública (E-64, provado com
// `sha256=b58fdd68…` contra o run real `9bc2fb3d`). E a tela não mostrava
// nada: `grep -rn "intelligence-v2/conversas" src` devolvia VAZIO. Tudo
// funcionava menos a única coisa que o consultor pediu.
//
// ⚠️ **Não confundir com `ui-registry/`**: aquilo é cartão de DADO (uma tool
// devolve `ui.component` e a bolha monta um componente Vue). Aqui é ARQUIVO —
// `.xlsx`, `.pdf`, `.py` — que sai do workspace do agente para o disco do
// consultor. São dois assuntos com nome parecido, e `store/artefato.js` é do
// primeiro, não deste.
//
// ## As duas fontes, e por que as duas precisam existir
//
// - **AO VIVO** (`aoVivo`): o evento SSE `arquivo` cai no canal transiente
//   `arquivoStreaming` — é o que desenha o cartão enquanto o turno roda.
// - **PERSISTIDA** (`ancoraDoRun` + `idRun`): `GET /conversas/{id}/artefatos`,
//   pelo slice `store/artefatos-do-workspace.js`. Sem ela o cartão SOME no
//   F5, que é literalmente o MUST-1 do cartão de fonte web — esta base já
//   pagou esse defeito uma vez, com o mesmo formato.
//
// A bolha VIVA lê só o canal transiente; a persistida lê só a lista. Nunca as
// duas somadas: na virada do turno (streaming → mensagem gravada) o mesmo
// arquivo apareceria em dois cartões.
//
// ## `manifesto` nunca vira cartão
//
// `d_arquivo.tipo` ∈ {`anexo`, `artefato`, `manifesto`}. O `manifesto` é o
// snapshot interno que restaura o `cwd` entre turnos (`ManifestoArquivos`);
// o backend o exclui da lista E devolve 404 no download
// (`TIPOS_ENTREGAVEIS`, AC-4.8). O canal SSE, porém, NÃO passa por essa
// guarda — ele publica o evento cru, e medido no run real `9bc2fb3d` há um
// `manifesto` de 8.443 bytes entre os eventos `arquivo`. Sem repetir a
// allowlist aqui, o turno ao vivo desenharia um cartão cujo clique dá 404.
//
// Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
import { apiArtefatosConversa } from '@modules/aawz-intelligence-v2/api/apiArtefatosConversa'
import { STORE_PRINCIPAL } from '@modules/aawz-intelligence-v2/api/chat-event-handlers'
import { formatFileSize } from '@utils/Format'

// A MESMA allowlist de `core/artefatos_autorizacao.py::TIPOS_ENTREGAVEIS`.
// Duplicada aqui de propósito: é o canal SSE que não tem a guarda do backend
// (ver o cabeçalho). O par negativo está no `.unit.js`.
const TIPOS_ENTREGAVEIS = ['anexo', 'artefato']

// Piso do spinner do clique. Sem ele o feedback pisca e some num arquivo
// pequeno (a planilha do run real tem 6,4 KB), e o consultor não tem certeza
// de que clicou. ⚠️ O piso segura só o SPINNER — o arquivo vai para o disco
// assim que a resposta chega, sem atraso.
const MS_MINIMO_DE_LOADING = 600

/**
 * Extensão exibida no cartão, derivada do NOME do recibo.
 *
 * Do nome, e não do `content_type`: medido no run real, a planilha `.xlsx`
 * está gravada como `application/octet-stream` — o tipo MIME do objeto não
 * diz o formato. O backend faz o mesmo corte (`atachment_filename.rsplit`)
 * para montar o `extension` do download.
 *
 * @param {String} nome nome do arquivo entregue.
 * @returns {String} extensão em maiúsculas, ou string vazia quando não há.
 */
function extensaoDoNome(nome) {
  if (typeof nome !== 'string') return ''
  const partes = nome.split('.')
  if (partes.length < 2) return ''
  return partes[partes.length - 1].toUpperCase()
}

export default {
  name: 'IntelligenceV2ChatMessageArtefatos',
  props: {
    // Conversa aberta — a rota de download é por conversa (o gate D11 do
    // backend autoriza pela POSSE dela, não pelo objeto solto).
    idConversa: {
      type: String,
      default: '',
    },
    // `message.id_run` desta bolha. É o que casa a bolha com o artefato:
    // `listar_artefatos` devolve `id_run` por item (E-72), e
    // `resource_messages.py` já devolvia `id_run` por mensagem.
    idRun: {
      type: String,
      default: '',
    },
    // A bolha persistida que REPRESENTA o run — só ela desenha os artefatos
    // da lista. Um run pode gravar mais de uma mensagem de assistente
    // (medido: 5 de 3.725 no banco local), e sem esta trava o mesmo arquivo
    // sairia duplicado, uma vez por bolha. Default `false`: bolha que não se
    // declara âncora não desenha — não mostrar é melhor que mostrar duas.
    ancoraDoRun: {
      type: Boolean,
      default: false,
    },
    // Vivacidade da BOLHA (o mesmo `isStreaming` que `chat-message.vue` já
    // calcula). Só ela lê o canal transiente, pelo mesmo motivo do MUST-2 do
    // cartão de fonte web: `arquivoStreaming` é por TURNO, e este componente
    // é montado em CADA bolha — sem escopo, toda resposta anterior da
    // conversa mostraria o arquivo do turno em voo. Default `false` =
    // fail-closed.
    aoVivo: {
      type: Boolean,
      default: false,
    },
    // Esta bolha acabou de nascer do `end` do turno (`chat-message.vue::
    // criadaAoVivo`, que lê o `duracao_s` carimbado pelo `_onEventEnd`; o
    // histórico vem sem ele).
    //
    // ⚠️ **É o que impede o cartão de SUMIR no fim do turno.** A lista da
    // conversa foi lida quando ela abriu — antes de este run gravar arquivo
    // nenhum. Sem forçar a releitura aqui, o cartão aparece durante o
    // streaming (canal transiente) e some no instante em que a bolha
    // persistida o substitui, que é justamente quando o consultor vai
    // clicar. Só a bolha recém-nascida força: o histórico inteiro relendo a
    // lista seria um GET por bolha ao abrir a conversa.
    recemEntregue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // `id_objeto` em download agora — `''` quando nenhum.
      baixando: '',
      timerDoLoading: null,
    }
  },
  computed: {
    // Leitura direta do store, sem `mapGetters` — mesmo padrão de
    // `chat-message-fontes-web.vue`: os units montam com um `$store` plano.
    getters() {
      return (this.$store && this.$store.getters) || {}
    },
    // O canal do turno em voo. Só a bolha viva o lê.
    arquivosEmStreaming() {
      if (!this.aoVivo) return []
      return this.getters[STORE_PRINCIPAL + '/arquivoStreaming'] || []
    },
    // A lista persistida, filtrada pelo run DESTA bolha.
    //
    // A comparação de `idConversa` não é zelo redundante: entre trocar de
    // conversa e a lista nova chegar, o store ainda tem a lista da conversa
    // ANTERIOR — sem o corte, o arquivo de um cliente apareceria na conversa
    // de outro.
    arquivosPersistidos() {
      if (this.aoVivo || !this.ancoraDoRun || !this.idRun) return []
      if (this.getters[STORE_PRINCIPAL + '/artefatosDoWorkspaceIdConversa'] !== this.idConversa) {
        return []
      }
      const lista = this.getters[STORE_PRINCIPAL + '/artefatosDoWorkspace'] || []
      return lista.filter((item) => item && item.id_run === this.idRun)
    },
    // UMA das duas fontes, nunca as duas somadas (ver o cabeçalho).
    arquivosBrutos() {
      return this.aoVivo ? this.arquivosEmStreaming : this.arquivosPersistidos
    },
    // Um cartão por `id_objeto`, na ordem de chegada. A PRIMEIRA ocorrência
    // vence: o relay pode reenviar o evento numa reconexão por cursor, e
    // trocar o cartão debaixo do olho do consultor seria pior que ignorar.
    artefatos() {
      const vistos = {}
      const lista = []
      this.arquivosBrutos.forEach((bruto) => {
        if (!bruto || !bruto.id_objeto) return
        if (TIPOS_ENTREGAVEIS.indexOf(bruto.tipo) === -1) return
        if (vistos[bruto.id_objeto]) return
        vistos[bruto.id_objeto] = true
        lista.push({
          idObjeto: bruto.id_objeto,
          // Nome ausente não deixa o cartão em branco: sem ele o consultor
          // veria um botão que não diz o que baixa.
          nome: bruto.nome || 'arquivo',
          detalhe: this.detalheDoArtefato(bruto),
        })
      })
      return lista
    },
    mostrar() {
      return this.artefatos.length > 0
    },
  },
  created() {
    // Quem pede a lista é a BOLHA, e o guard do slice faz disso UMA
    // requisição por conversa (N bolhas, 1 GET). Na view seriam três lugares
    // a lembrar — `view-chat`, `view-chat-imersivo` e o `chat-ia` de página
    // cheia —, e esquecer um deixa a tela sem o arquivo sem erro nenhum.
    if (this.aoVivo || !this.ancoraDoRun || !this.idConversa) return
    this.$store.dispatch(STORE_PRINCIPAL + '/carregarArtefatosDoWorkspace', {
      idConversa: this.idConversa,
      // `forcar` só na bolha que o `end` acabou de criar — ver a prop.
      forcar: this.recemEntregue,
    })
  },
  beforeDestroy() {
    if (this.timerDoLoading) clearTimeout(this.timerDoLoading)
  },
  methods: {
    /**
     * Linha secundária do cartão: formato, tamanho e versão.
     *
     * @param {Object} bruto item do canal SSE ou da lista persistida.
     * @returns {String} ex.: `'XLSX · 6.4 KB'`, `'PY · 4.1 KB · versão 2'`.
     */
    detalheDoArtefato(bruto) {
      const partes = []
      const extensao = extensaoDoNome(bruto.nome)
      if (extensao) partes.push(extensao)
      // `formatFileSize` devolve `'0 B'` para ausente/zero — honesto, e nunca
      // "NaN undefined" na tela.
      partes.push(formatFileSize(bruto.bytes))
      // Versão 1 é a regra; só a partir da 2ª o número informa algo (o agente
      // regravou o arquivo no mesmo run).
      if (bruto.versao > 1) partes.push('versão ' + bruto.versao)
      return partes.join(' · ')
    },
    /**
     * Baixa o artefato e dá o feedback — a ação que faltava.
     *
     * @param {Object} artefato item já normalizado por `artefatos`.
     */
    async baixar(artefato) {
      if (this.baixando) return
      this.baixando = artefato.idObjeto
      const inicio = Date.now()
      const r = await apiArtefatosConversa.baixar({
        idConversa: this.idConversa,
        idObjeto: artefato.idObjeto,
      })
      // Sucesso é o arquivo no disco, não um texto verde por cima dele —
      // snack só no erro (`$showSnack(r)` já infere texto e cor da resposta).
      if (!r || r.status !== 200) this.$showSnack(r)
      this.desligarLoading(inicio)
    },
    desligarLoading(inicio) {
      const restante = MS_MINIMO_DE_LOADING - (Date.now() - inicio)
      if (restante <= 0) {
        this.baixando = ''
        return
      }
      this.timerDoLoading = setTimeout(() => {
        this.baixando = ''
        this.timerDoLoading = null
      }, restante)
    },
  },
}
</script>

<template>
  <div v-if="mostrar" class="chat-message-artefatos mb-4">
    <div class="chat-message-artefatos__titulo az-text-secondary az-size-12 mb-2">
      Arquivos entregues nesta resposta
    </div>
    <button
      v-for="artefato in artefatos"
      :key="artefato.idObjeto"
      type="button"
      class="chat-message-artefatos__cartao d-flex align-center az-gap-small px-3 py-2 mb-2 w-100 text-left"
      :disabled="Boolean(baixando)"
      data-test="artefato-cartao"
      @click="baixar(artefato)"
    >
      <icon-svg
        name="document-text"
        :size="16"
        color="var(--neutral-600)"
        class="flex-shrink-0"
      />
      <span class="chat-message-artefatos__texto d-flex flex-column">
        <span
          class="chat-message-artefatos__nome az-text-primary az-size-14 font-weight-medium"
          data-test="artefato-nome"
        >{{ artefato.nome }}</span>
        <span class="az-text-secondary az-size-12 az-muted" data-test="artefato-detalhe">
          {{ artefato.detalhe }}
        </span>
      </span>
      <v-progress-circular
        v-if="baixando === artefato.idObjeto"
        indeterminate
        :size="16"
        :width="2"
        color="primary"
        class="flex-shrink-0"
        data-test="artefato-baixando"
      />
      <icon-svg
        v-else
        name="download"
        :size="16"
        color="var(--neutral-600)"
        class="flex-shrink-0"
        data-test="artefato-icone-download"
      />
    </button>
  </div>
</template>

<style lang="sass" scoped>
.chat-message-artefatos
  &__cartao
    // `display: flex` fica só na classe `d-flex` do template — declará-lo de
    // novo aqui duplicaria a utility do Vuetify (regra do design-system).
    border: 1px solid var(--border-hairline)
    border-radius: var(--radius-md)
    background: var(--white)
    cursor: pointer
    transition: border-color .15s ease

    &:hover:not(:disabled)
      border-color: var(--primary)

    &:disabled
      cursor: default

  &__texto
    // Ocupa a folga entre o ícone do formato e o do download — sem isto a
    // ação da direita encosta no nome em arquivo de nome curto.
    flex: 1
    min-width: 0

  &__nome
    word-break: break-word
</style>
